import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BadgeImageUpload } from "./BadgeImageUpload";
import { Search, Users } from "lucide-react";

interface Badge {
  id: string;
  name_it: string;
  name_en: string | null;
  name_es: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  image_url: string | null;
  badge_type: string;
  is_active: boolean;
}

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface BadgeFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  badge: Badge | null;
}

export const BadgeFormModal = ({ open, onClose, onSuccess, badge }: BadgeFormModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [formData, setFormData] = useState({
    name_it: "",
    name_en: "",
    name_es: "",
    description_it: "",
    description_en: "",
    description_es: "",
    image_url: "",
    badge_type: "custom",
    is_active: true,
  });

  useEffect(() => {
    if (open) {
      if (badge) {
        setFormData({
          name_it: badge.name_it || "",
          name_en: badge.name_en || "",
          name_es: badge.name_es || "",
          description_it: badge.description_it || "",
          description_en: badge.description_en || "",
          description_es: badge.description_es || "",
          image_url: badge.image_url || "",
          badge_type: badge.badge_type || "custom",
          is_active: badge.is_active ?? true,
        });
        loadAssignedUsers(badge.id);
      } else {
        resetForm();
      }
      loadUsers();
    }
  }, [open, badge]);

  const resetForm = () => {
    setFormData({
      name_it: "",
      name_en: "",
      name_es: "",
      description_it: "",
      description_en: "",
      description_es: "",
      image_url: "",
      badge_type: "custom",
      is_active: true,
    });
    setSelectedUsers([]);
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-users-admin", {
        body: {},
      });

      if (error) throw error;

      setUsers(data.users || []);
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadAssignedUsers = async (badgeId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_badges")
        .select("user_id")
        .eq("badge_id", badgeId);

      if (error) throw error;

      setSelectedUsers((data || []).map(ub => ub.user_id));
    } catch (error) {
      console.error("Error loading assigned users:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name_it.trim()) {
      toast({ title: t("admin.badges.form.nameRequired"), variant: "error" });
      return;
    }

    setLoading(true);
    try {
      let badgeId = badge?.id;

      if (badge) {
        // Update existing badge
        const { error } = await supabase
          .from("badges")
          .update({
            name_it: formData.name_it,
            name_en: formData.name_en || null,
            name_es: formData.name_es || null,
            description_it: formData.description_it || null,
            description_en: formData.description_en || null,
            description_es: formData.description_es || null,
            image_url: formData.image_url || null,
            badge_type: formData.badge_type,
            is_active: formData.is_active,
          })
          .eq("id", badge.id);

        if (error) throw error;
      } else {
        // Create new badge
        const { data, error } = await supabase
          .from("badges")
          .insert({
            name_it: formData.name_it,
            name_en: formData.name_en || null,
            name_es: formData.name_es || null,
            description_it: formData.description_it || null,
            description_en: formData.description_en || null,
            description_es: formData.description_es || null,
            image_url: formData.image_url || null,
            badge_type: formData.badge_type,
            is_active: formData.is_active,
            created_by: user?.id,
          })
          .select()
          .single();

        if (error) throw error;
        badgeId = data.id;
      }

      // Update user assignments
      if (badgeId) {
        // Delete old assignments
        await supabase
          .from("user_badges")
          .delete()
          .eq("badge_id", badgeId);

        // Insert new assignments
        if (selectedUsers.length > 0) {
          const assignments = selectedUsers.map(userId => ({
            user_id: userId,
            badge_id: badgeId,
            assigned_by: user?.id,
          }));

          const { error: assignError } = await supabase
            .from("user_badges")
            .insert(assignments);

          if (assignError) throw assignError;
        }
      }

      toast({ 
        title: badge ? t("admin.badges.updateSuccess") : t("admin.badges.createSuccess"), 
        variant: "success" 
      });
      onSuccess();
    } catch (error) {
      console.error("Error saving badge:", error);
      toast({ 
        title: badge ? t("admin.badges.updateError") : t("admin.badges.createError"), 
        variant: "error" 
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredUsers = users.filter(u => {
    const query = searchQuery.toLowerCase();
    return (
      u.email?.toLowerCase().includes(query) ||
      u.first_name?.toLowerCase().includes(query) ||
      u.last_name?.toLowerCase().includes(query)
    );
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {badge ? t("admin.badges.form.editTitle") : t("admin.badges.form.createTitle")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload */}
          <div className="flex justify-center">
            <BadgeImageUpload
              currentUrl={formData.image_url}
              onUploadComplete={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
              onRemove={() => setFormData(prev => ({ ...prev, image_url: "" }))}
            />
          </div>

          {/* Badge Type */}
          <div>
            <Label className="font-bold">{t("admin.badges.form.type")}</Label>
            <select
              className="w-full border-[3px] border-dark p-3 bg-card mt-2"
              value={formData.badge_type}
              onChange={(e) => setFormData(prev => ({ ...prev, badge_type: e.target.value }))}
            >
              <option value="custom">{t("admin.badges.form.typeCustom")}</option>
              <option value="pro">{t("admin.badges.form.typePro")}</option>
              <option value="plus">{t("admin.badges.form.typePlus")}</option>
            </select>
          </div>

          {/* Multilingual Names */}
          <div>
            <Label className="font-bold mb-2 block">{t("admin.badges.form.name")}</Label>
            <Tabs defaultValue="it" className="w-full">
              <TabsList className="mb-2">
                <TabsTrigger value="it">🇮🇹 IT</TabsTrigger>
                <TabsTrigger value="en">🇬🇧 EN</TabsTrigger>
                <TabsTrigger value="es">🇪🇸 ES</TabsTrigger>
              </TabsList>
              <TabsContent value="it">
                <BrutalInput
                  placeholder={t("admin.badges.form.namePlaceholder") + " (IT) *"}
                  value={formData.name_it}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_it: e.target.value }))}
                  required
                />
              </TabsContent>
              <TabsContent value="en">
                <BrutalInput
                  placeholder={t("admin.badges.form.namePlaceholder") + " (EN)"}
                  value={formData.name_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                />
              </TabsContent>
              <TabsContent value="es">
                <BrutalInput
                  placeholder={t("admin.badges.form.namePlaceholder") + " (ES)"}
                  value={formData.name_es}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_es: e.target.value }))}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Multilingual Descriptions */}
          <div>
            <Label className="font-bold mb-2 block">{t("admin.badges.form.description")}</Label>
            <Tabs defaultValue="it" className="w-full">
              <TabsList className="mb-2">
                <TabsTrigger value="it">🇮🇹 IT</TabsTrigger>
                <TabsTrigger value="en">🇬🇧 EN</TabsTrigger>
                <TabsTrigger value="es">🇪🇸 ES</TabsTrigger>
              </TabsList>
              <TabsContent value="it">
                <BrutalTextarea
                  placeholder={t("admin.badges.form.descriptionPlaceholder") + " (IT)"}
                  value={formData.description_it}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_it: e.target.value }))}
                  className="h-20"
                />
              </TabsContent>
              <TabsContent value="en">
                <BrutalTextarea
                  placeholder={t("admin.badges.form.descriptionPlaceholder") + " (EN)"}
                  value={formData.description_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                  className="h-20"
                />
              </TabsContent>
              <TabsContent value="es">
                <BrutalTextarea
                  placeholder={t("admin.badges.form.descriptionPlaceholder") + " (ES)"}
                  value={formData.description_es}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_es: e.target.value }))}
                  className="h-20"
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
            />
            <Label htmlFor="is_active" className="font-bold">
              {t("admin.badges.form.active")}
            </Label>
          </div>

          {/* User Assignment Section */}
          <div className="border-[3px] border-dark p-4">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5" />
              <Label className="font-bold text-lg">{t("admin.badges.form.assignUsers")}</Label>
              <span className="text-sm text-muted-foreground">
                ({selectedUsers.length} {t("admin.badges.form.selected")})
              </span>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <BrutalInput
                placeholder={t("admin.badges.form.searchUsers")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Users List */}
            <ScrollArea className="h-64 border-[2px] border-dark">
              {loadingUsers ? (
                <div className="p-4 text-center text-muted-foreground">
                  {t("admin.badges.form.loadingUsers")}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  {t("admin.badges.form.noUsers")}
                </div>
              ) : (
                <div className="divide-y-[2px] divide-dark">
                  {filteredUsers.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-3 p-3 hover:bg-surface cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedUsers.includes(u.id)}
                        onCheckedChange={() => toggleUser(u.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">
                          {u.first_name || u.last_name 
                            ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                            : t("admin.badges.form.noName")}
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          {u.email}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <BrutalButton
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              {t("admin.badges.form.cancel")}
            </BrutalButton>
            <BrutalButton
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading 
                ? t("admin.badges.form.saving") 
                : badge 
                  ? t("admin.badges.form.update") 
                  : t("admin.badges.form.create")}
            </BrutalButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};